import { useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";
import { withClientContext } from "~/apollo/client";
import { useGetDdqProdOpsQueueQuery } from "~/graphql/supergraphHooks";
import type { DdqQueueRow } from "./types";

// This hook scans pages looking for one specific row, independent of the Grid's own
// user-configurable page size -- see useDdqQueueFilters for that one.
const PAGE_SCAN_LIMIT = 100;

export type UseDdqProdOpsQueueRowResult = {
  row: DdqQueueRow | undefined;
  isLoading: boolean;
  error?: ApolloError;
  refetch: () => void;
};

// The queue endpoint is paginated for the grid (see useDdqProdOpsQueue); a direct wizard URL
// needs one specific row regardless of where it falls in a backlog bigger than one page, so
// this walks the remaining pages (same fetchMore + updateQuery pattern as
// useFootnoteCustomFields) until the row turns up or the queue is exhausted.
export const useDdqProdOpsQueueRow = (targetId: string): UseDdqProdOpsQueueRowResult => {
  const {
    data,
    loading,
    error: queryError,
    fetchMore,
    refetch,
  } = useGetDdqProdOpsQueueQuery({
    context: withClientContext("federation"),
    variables: { limit: PAGE_SCAN_LIMIT, offset: 0 },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
  });

  // A failed page fetch stops pagination: surfacing the error (and keeping the rows we already have).
  const [pageError, setPageError] = useState<ApolloError>();
  // Reset during render (not an effect) when targetId changes, since the Wizard route can swap
  // it without remounting.
  const [scannedTargetId, setScannedTargetId] = useState(targetId);
  if (targetId !== scannedTargetId) {
    setScannedTargetId(targetId);
    setPageError(undefined);
  }

  const items = data?.ddqProdOpsQueue?.items ?? [];
  const totalCount = data?.ddqProdOpsQueue?.totalCount ?? 0;
  const row = items.find((item) => item.targetId === targetId);
  const error = queryError ?? pageError;
  const hasMore = items.length < totalCount && error == null;
  const shouldFetchNextPage = !row && !loading && hasMore;

  useEffect(() => {
    if (!shouldFetchNextPage) return;
    let active = true;
    void fetchMore({
      variables: { offset: items.length },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;
        return {
          ddqProdOpsQueue: {
            ...fetchMoreResult.ddqProdOpsQueue,
            items: [
              ...previous.ddqProdOpsQueue.items,
              ...fetchMoreResult.ddqProdOpsQueue.items,
            ],
          },
        };
      },
    }).catch((err: unknown) => {
      if (active) {
        setPageError(
          err instanceof ApolloError ? err : new ApolloError({ errorMessage: String(err) }),
        );
      }
    });
    return () => {
      active = false;
    };
  }, [shouldFetchNextPage, items.length, fetchMore]);

  return {
    row,
    isLoading: loading || shouldFetchNextPage,
    error,
    refetch: () => {
      setPageError(undefined);
      void refetch();
    },
  };
};
